import type { ApiError } from '../types/auth';

const API_BASE_URL =  '/api/v1';

// Keys para localStorage
const ACCESS_TOKEN_KEY = 'imi_access_token';
const REFRESH_TOKEN_KEY = 'imi_refresh_token';

// Gestión de tokens
export const tokenManager = {
  getAccessToken: (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),
  
  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  
  setAccessToken: (accessToken: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  },
  
  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
  
  hasTokens: (): boolean => {
    return !!localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  // Devuelve el timestamp de expiración (exp) del access token en segundos Unix.
  // Decodifica el payload base64 del JWT sin dependencias externas.
  // Devuelve null si no hay token o no se puede decodificar.
  getTokenExpiry: (): number | null => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) return null;
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return typeof decoded.exp === 'number' ? decoded.exp : null;
    } catch {
      return null;
    }
  },
};

// Clase de error personalizada
export class ApiRequestError extends Error {
  statusCode: number;
  
  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.statusCode = statusCode;
  }
}

// Variable para evitar múltiples refreshes simultáneos
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// Función para refrescar el token
async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenManager.getRefreshToken();
  
  if (!refreshToken) {
    throw new ApiRequestError('No refresh token available', 401);
  }
  
  const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken }),
  });
  
  if (!response.ok) {
    tokenManager.clearTokens();
    throw new ApiRequestError('Session expired', 401);
  }
  
  const data = await response.json();
  tokenManager.setAccessToken(data.accessToken);
  
  return data.accessToken;
}

// Cliente API principal
interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

export async function apiClient<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options;
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...fetchOptions.headers,
  };
  
  // Añadir token de autorización si existe y no se salta auth
  if (!skipAuth) {
    const accessToken = tokenManager.getAccessToken();
    if (accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
    }
  }
  
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
  
  let response = await fetch(url, {
    ...fetchOptions,
    headers,
  });
  
  // Si el token expiró, intentar refrescar
  if (response.status === 401 && !skipAuth && tokenManager.getRefreshToken()) {
    try {
      // Evitar múltiples refreshes simultáneos
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken();
      }
      
      const newAccessToken = await refreshPromise;
      isRefreshing = false;
      refreshPromise = null;
      
      // Reintentar la petición original con el nuevo token
      (headers as Record<string, string>)['Authorization'] = `Bearer ${newAccessToken}`;
      response = await fetch(url, {
        ...fetchOptions,
        headers,
      });
    } catch {
      isRefreshing = false;
      refreshPromise = null;
      // Redirigir al login
      window.location.href = '/login';
      throw new ApiRequestError('Session expired', 401);
    }
  }
  
  // Manejar errores
  if (!response.ok) {
    let errorMessage = 'An error occurred';
    
    try {
      const errorData: ApiError = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    
    throw new ApiRequestError(errorMessage, response.status);
  }
  
  // Manejar respuesta vacía (204 No Content o body vacío en 200)
  if (response.status === 204) {
    return {} as T;
  }

  const text = await response.text();
  if (!text.trim()) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return {} as T;
  }
}

// Métodos de conveniencia
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    apiClient<T>(endpoint, { ...options, method: 'GET' }),
    
  post: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    apiClient<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),
    
  put: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    apiClient<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),
    
  patch: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    apiClient<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),
    
  delete: <T>(endpoint: string, options?: RequestOptions) =>
    apiClient<T>(endpoint, { ...options, method: 'DELETE' }),
};

export default api;
